import { useState, useEffect } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import {
  Database, GitBranch, CheckCircle2, Clock, AlertTriangle, Settings,
  ArrowRight, FileText, Download, RefreshCw, Server, Shield, Layers,
  Lightbulb, ChevronRight, Circle, Diamond, Square, Hexagon
} from "lucide-react";
import { toast } from "sonner";
import { jsPDF } from "jspdf";
import { applyPlugin } from "jspdf-autotable";
import mermaid from "mermaid";
import { useRef, useCallback } from "react";

applyPlugin(jsPDF);

mermaid.initialize({ startOnLoad: false, theme: "default", securityLevel: "loose" });

const MermaidDiagram = ({ chart, id }) => {
  const containerRef = useRef(null);
  const rendered = useRef(false);

  const renderChart = useCallback(async (node) => {
    if (!node || !chart || rendered.current) return;
    containerRef.current = node;
    rendered.current = true;
    try {
      const uniqueId = `mermaid-${id}-${Date.now()}`;
      const { svg } = await mermaid.render(uniqueId, chart);
      node.innerHTML = svg;
      const svgEl = node.querySelector("svg");
      if (svgEl) {
        svgEl.style.maxWidth = "100%";
        svgEl.style.height = "auto";
      }
    } catch (e) {
      node.innerHTML = `<pre class="text-xs text-slate-600 bg-slate-50 p-4 rounded overflow-auto whitespace-pre">${chart}</pre>`;
    }
  }, [chart, id]);

  // Reset rendered flag when chart changes
  if (rendered.current && containerRef.current) {
    rendered.current = false;
  }

  return <div ref={renderChart} className="overflow-auto" />;
};

const SECCIONES = [
  { id: "der", nombre: "Entidad-Relación", icon: Database, color: "#2563eb" },
  { id: "general", nombre: "Flujograma General", icon: GitBranch, color: "#7c3aed" },
  { id: "flujos", nombre: "Flujos Detallados", icon: Layers, color: "#059669" },
  { id: "diagramas", nombre: "Diagramas Mermaid", icon: Hexagon, color: "#8b5cf6" },
  { id: "ready", nombre: "Ready del Sistema", icon: CheckCircle2, color: "#f59e0b" },
  { id: "recomendaciones", nombre: "Recomendaciones", icon: Lightbulb, color: "#ea580c" }
];

const FlujogramasPage = ({ API, token }) => {
  const headers = { Authorization: `Bearer ${token}` };
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [seccionActiva, setSeccionActiva] = useState("der");
  const [flujoActivo, setFlujoActivo] = useState(0);
  const [diagramaActivo, setDiagramaActivo] = useState("general_sistema");

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const resp = await axios.get(`${API}/definiciones/flujogramas`, { headers });
      setData(resp.data);
    } catch { toast.error("Error al cargar documentación"); }
    setLoading(false);
  };

  const exportPDF = () => {
    if (!data) return;
    const doc = new jsPDF();
    const pw = doc.internal.pageSize.getWidth();
    let y = 0;

    // Cover
    doc.setFillColor(30, 41, 59);
    doc.rect(0, 0, pw, 100, "F");
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(28);
    doc.text("SIRET", pw / 2, 35, { align: "center" });
    doc.setFontSize(14);
    doc.text("Documentacion Tecnica Completa", pw / 2, 50, { align: "center" });
    doc.setFontSize(10);
    doc.text("Sistema de Recaudacion Tributaria Municipal", pw / 2, 63, { align: "center" });
    doc.text(`Generado: ${new Date().toLocaleDateString("es-VE")}`, pw / 2, 90, { align: "center" });

    // DER
    doc.setTextColor(30, 41, 59);
    y = 115;
    doc.setFontSize(16);
    doc.text("1. Diagrama Entidad-Relacion", 14, y);
    y += 10;
    data.der.forEach(e => {
      if (y > 250) { doc.addPage(); y = 20; }
      doc.setFontSize(11);
      doc.setTextColor(37, 99, 235);
      doc.text(`${e.nombre}`, 14, y);
      doc.setTextColor(100, 100, 100);
      doc.setFontSize(8);
      doc.text(e.descripcion, 14, y + 5);
      y += 8;
      doc.autoTable({
        startY: y,
        margin: { left: 14, right: 14 },
        head: [["Campo", "Tipo", "Notas"]],
        body: e.campos.map(c => [c.nombre, c.tipo, [c.pk && "PK", c.unique && "UNIQUE", c.fk && `FK→${c.fk}`, c.valores && `[${c.valores}]`].filter(Boolean).join(", ") || "-"]),
        styles: { fontSize: 7 },
        headStyles: { fillColor: [37, 99, 235] }
      });
      y = doc.lastAutoTable.finalY + 8;
    });

    // Flujos
    doc.addPage();
    y = 20;
    doc.setFontSize(16);
    doc.setTextColor(30, 41, 59);
    doc.text("3. Flujogramas Detallados", 14, y);
    y += 10;
    data.flujos_detallados.forEach((f, fi) => {
      if (y > 240) { doc.addPage(); y = 20; }
      doc.setFontSize(12);
      doc.setTextColor(30, 41, 59);
      doc.text(`${fi + 1}. ${f.nombre}`, 14, y);
      y += 7;
      doc.setFontSize(8);
      f.pasos.forEach((p, pi) => {
        if (y > 275) { doc.addPage(); y = 20; }
        const prefix = p.tipo === "decision" ? "[?]" : p.tipo === "bd" ? "[BD]" : p.tipo === "inicio" ? "[>]" : p.tipo === "fin" ? "[=]" : "[*]";
        doc.setTextColor(80, 80, 80);
        doc.text(`  ${prefix} ${p.texto}`, 18, y);
        if (p.si) { doc.text(`     Si: ${p.si} | No: ${p.no}`, 22, y + 4); y += 4; }
        y += 5;
      });
      y += 5;
    });

    // Ready
    doc.addPage();
    y = 20;
    doc.setFontSize(16);
    doc.setTextColor(30, 41, 59);
    doc.text("4. Ready del Sistema", 14, y);
    y += 10;
    doc.autoTable({
      startY: y,
      head: [["Regla de Negocio", "Estado"]],
      body: data.ready.reglas_negocio.map(r => [r.regla, r.estado]),
      styles: { fontSize: 8 },
      headStyles: { fillColor: [245, 158, 11] },
      didParseCell: (d) => {
        if (d.section === "body" && d.column.index === 1) {
          d.cell.styles.textColor = d.cell.raw === "completado" ? [22, 163, 74] : [239, 68, 68];
        }
      }
    });

    doc.save(`SIRET_Documentacion_Tecnica_${new Date().toISOString().split("T")[0]}.pdf`);
    toast.success("PDF descargado");
  };

  if (!data) return <div className="flex items-center justify-center py-20 text-slate-400">{loading ? "Cargando..." : "Sin datos"}</div>;

  const sec = SECCIONES.find(s => s.id === seccionActiva);

  return (
    <div className="space-y-4" data-testid="flujogramas-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-slate-800 flex items-center gap-2">
            <GitBranch className="w-6 h-6 text-violet-600" />
            Documentación Técnica - SIRET
          </h2>
          <p className="text-xs text-slate-500 mt-1">{data.total_colecciones} colecciones | Generado: {new Date(data.generado).toLocaleString("es-VE")}</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={fetchData} disabled={loading} className="gap-2">
            <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} /> Actualizar
          </Button>
          <Button variant="outline" onClick={exportPDF} className="gap-2">
            <Download className="w-4 h-4" /> PDF
          </Button>
        </div>
      </div>

      {/* Navigation + Content */}
      <div className="grid grid-cols-12 gap-4">
        {/* Sidebar */}
        <div className="col-span-3">
          <Card>
            <CardContent className="p-2 space-y-1">
              {SECCIONES.map(s => {
                const Icon = s.icon;
                return (
                  <button key={s.id} onClick={() => setSeccionActiva(s.id)}
                    className={`w-full text-left px-3 py-2.5 rounded-md text-sm transition-colors flex items-center gap-2 ${seccionActiva === s.id ? "text-white font-semibold" : "text-slate-600 hover:bg-slate-100"}`}
                    style={seccionActiva === s.id ? { backgroundColor: s.color } : {}}>
                    <Icon className="w-4 h-4 flex-shrink-0" />
                    {s.nombre}
                  </button>
                );
              })}
            </CardContent>
          </Card>

          {/* Stats */}
          <Card className="mt-3">
            <CardContent className="p-3">
              <p className="text-xs font-semibold text-slate-400 uppercase mb-2">Base de Datos</p>
              <div className="space-y-1 max-h-60 overflow-y-auto">
                {Object.entries(data.estadisticas_bd).sort((a,b) => b[1] - a[1]).map(([col, count]) => (
                  <div key={col} className="flex items-center justify-between text-xs">
                    <span className="text-slate-600 truncate max-w-[140px]">{col}</span>
                    <Badge variant="outline" className="text-xs ml-1">{count}</Badge>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Content */}
        <div className="col-span-9 space-y-4">
          {/* ===== DER ===== */}
          {seccionActiva === "der" && (
            <>
              <Card className="border-t-4" style={{ borderTopColor: sec.color }}>
                <CardContent className="p-5">
                  <h3 className="text-lg font-bold text-slate-800 mb-1">Diagrama Entidad-Relación</h3>
                  <p className="text-sm text-slate-500">{data.der.length} entidades principales del sistema</p>
                </CardContent>
              </Card>
              {data.der.map((e, i) => (
                <Card key={i}>
                  <CardContent className="p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <Database className="w-4 h-4 text-blue-600" />
                      <h4 className="font-bold text-blue-700">{e.nombre}</h4>
                      <Badge variant="outline" className="text-xs ml-auto">{e.campos.length} campos</Badge>
                    </div>
                    <p className="text-xs text-slate-500 mb-3">{e.descripcion}</p>
                    <table className="w-full text-xs">
                      <thead>
                        <tr className="bg-blue-600 text-white">
                          <th className="px-2 py-1.5 text-left font-semibold">Campo</th>
                          <th className="px-2 py-1.5 text-left font-semibold">Tipo</th>
                          <th className="px-2 py-1.5 text-left font-semibold">Notas</th>
                        </tr>
                      </thead>
                      <tbody>
                        {e.campos.map((c, ci) => (
                          <tr key={ci} className={ci % 2 === 0 ? "bg-slate-50" : ""}>
                            <td className="px-2 py-1 font-medium text-slate-800">{c.nombre}</td>
                            <td className="px-2 py-1 font-mono text-slate-600">{c.tipo}</td>
                            <td className="px-2 py-1">
                              {c.pk && <Badge className="bg-amber-100 text-amber-800 text-[10px] mr-1">PK</Badge>}
                              {c.unique && <Badge className="bg-blue-100 text-blue-700 text-[10px] mr-1">UNIQUE</Badge>}
                              {c.fk && <Badge className="bg-violet-100 text-violet-700 text-[10px] mr-1">FK: {c.fk}</Badge>}
                              {c.valores && <span className="text-slate-400">[{c.valores}]</span>}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    {e.relaciones?.length > 0 && (
                      <div className="mt-2 flex items-center gap-1 flex-wrap">
                        <span className="text-xs text-slate-400">Relaciones:</span>
                        {e.relaciones.map((r, ri) => (
                          <Badge key={ri} variant="outline" className="text-[10px]">{r}</Badge>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))}
            </>
          )}

          {/* ===== FLUJOGRAMA GENERAL ===== */}
          {seccionActiva === "general" && (
            <Card className="border-t-4" style={{ borderTopColor: sec.color }}>
              <CardContent className="p-5">
                <h3 className="text-lg font-bold text-slate-800 mb-4">Flujograma General del Sistema</h3>
                <div className="space-y-3">
                  {data.flujograma_general.map((mod, i) => (
                    <div key={i} className="flex items-center gap-3">
                      <div className="w-48 h-12 rounded-lg flex items-center justify-center text-white text-sm font-semibold shadow-md"
                        style={{ backgroundColor: mod.color }}>
                        {mod.nombre}
                      </div>
                      {mod.conecta.length > 0 && (
                        <>
                          <ArrowRight className="w-5 h-5 text-slate-400 flex-shrink-0" />
                          <div className="flex flex-wrap gap-1.5">
                            {mod.conecta.map((c, ci) => {
                              const target = data.flujograma_general.find(m => m.id === c);
                              return (
                                <Badge key={ci} variant="outline" className="text-xs px-2 py-0.5"
                                  style={{ borderColor: target?.color, color: target?.color }}>
                                  {target?.nombre || c}
                                </Badge>
                              );
                            })}
                          </div>
                        </>
                      )}
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* ===== FLUJOS DETALLADOS ===== */}
          {seccionActiva === "flujos" && (
            <>
              <div className="flex gap-2 flex-wrap">
                {data.flujos_detallados.map((f, i) => (
                  <Button key={i} size="sm" variant={flujoActivo === i ? "default" : "outline"}
                    style={flujoActivo === i ? { backgroundColor: f.color } : {}}
                    onClick={() => setFlujoActivo(i)} className="text-xs">
                    {f.nombre}
                  </Button>
                ))}
              </div>
              {data.flujos_detallados[flujoActivo] && (() => {
                const f = data.flujos_detallados[flujoActivo];
                return (
                  <Card className="border-t-4" style={{ borderTopColor: f.color }}>
                    <CardContent className="p-5">
                      <h3 className="text-lg font-bold text-slate-800 mb-4">{f.nombre}</h3>
                      <div className="space-y-0">
                        {f.pasos.map((p, pi) => {
                          const isLast = pi === f.pasos.length - 1;
                          return (
                            <div key={pi}>
                              <div className="flex items-start gap-3">
                                {/* Icon */}
                                <div className="flex flex-col items-center">
                                  {p.tipo === "inicio" && <div className="w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-bold" style={{ backgroundColor: f.color }}>{pi+1}</div>}
                                  {p.tipo === "fin" && <div className="w-8 h-8 rounded-full border-2 flex items-center justify-center" style={{ borderColor: f.color }}><CheckCircle2 className="w-4 h-4" style={{ color: f.color }} /></div>}
                                  {p.tipo === "accion" && <div className="w-8 h-8 rounded-md flex items-center justify-center text-white text-xs font-bold" style={{ backgroundColor: f.color }}>{pi+1}</div>}
                                  {p.tipo === "decision" && <div className="w-8 h-8 rotate-45 flex items-center justify-center text-white text-xs font-bold" style={{ backgroundColor: "#f59e0b" }}><span className="-rotate-45">?</span></div>}
                                  {p.tipo === "bd" && <div className="w-8 h-8 rounded-md flex items-center justify-center bg-violet-100"><Database className="w-4 h-4 text-violet-600" /></div>}
                                  {!isLast && <div className="w-0.5 h-6 bg-slate-200 my-1" />}
                                </div>
                                {/* Content */}
                                <div className="flex-1 pb-2">
                                  <div className={`rounded-lg px-3 py-2 text-sm ${
                                    p.tipo === "decision" ? "bg-amber-50 border border-amber-200" :
                                    p.tipo === "bd" ? "bg-violet-50 border border-violet-200" :
                                    p.tipo === "inicio" || p.tipo === "fin" ? "bg-slate-100 border border-slate-200 font-semibold" :
                                    "bg-white border border-slate-200"
                                  }`}>
                                    <span className="text-slate-700">{p.texto}</span>
                                    {p.si && (
                                      <div className="mt-1.5 flex gap-3 text-xs">
                                        <span className="text-green-600 font-medium">Si: {p.si}</span>
                                        <span className="text-red-500 font-medium">No: {p.no}</span>
                                      </div>
                                    )}
                                  </div>
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </CardContent>
                  </Card>
                );
              })()}
            </>
          )}

          {/* ===== DIAGRAMAS MERMAID ===== */}
          {seccionActiva === "diagramas" && data.diagramas_mermaid && (() => {
            const diagramas = Object.entries(data.diagramas_mermaid);
            const nombres = {
              general_sistema: "Sistema General",
              login: "Autenticación y Login",
              calculo: "Creación de Cálculo",
              enviar_caja: "Enviar a Caja",
              pago_validacion: "Validación de Pago",
              sync_obligaciones: "Sincronización Obligaciones LAE",
              reset_anual: "Reset Anual",
              reportes: "Generación de Reportes",
              acciones_masivas: "Acciones Masivas"
            };
            const colores = {
              general_sistema: "#1e293b", login: "#1e293b", calculo: "#2563eb", enviar_caja: "#f59e0b",
              pago_validacion: "#22c55e", sync_obligaciones: "#7c3aed", reset_anual: "#ef4444",
              reportes: "#6d28d9", acciones_masivas: "#ea580c"
            };
            return (
              <>
                <div className="flex gap-2 flex-wrap">
                  {diagramas.map(([key]) => (
                    <Button key={key} size="sm" variant={diagramaActivo === key ? "default" : "outline"}
                      style={diagramaActivo === key ? { backgroundColor: colores[key] || "#6b7280" } : {}}
                      onClick={() => setDiagramaActivo(key)} className="text-xs">
                      {nombres[key] || key}
                    </Button>
                  ))}
                </div>
                <Card className="border-t-4" style={{ borderTopColor: colores[diagramaActivo] || "#6b7280" }}>
                  <CardContent className="p-5">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-lg font-bold text-slate-800">{nombres[diagramaActivo] || diagramaActivo}</h3>
                      <Button variant="outline" size="sm" className="gap-2 text-xs"
                        onClick={() => {
                          const svgEl = document.querySelector(`#diagram-container svg`);
                          if (svgEl) {
                            const svgData = new XMLSerializer().serializeToString(svgEl);
                            const blob = new Blob([svgData], { type: "image/svg+xml" });
                            const url = URL.createObjectURL(blob);
                            const a = document.createElement("a");
                            a.href = url; a.download = `SIRET_${diagramaActivo}.svg`; a.click();
                            URL.revokeObjectURL(url);
                            toast.success("SVG descargado");
                          }
                        }}>
                        <Download className="w-3.5 h-3.5" /> SVG
                      </Button>
                    </div>
                    <div id="diagram-container" className="bg-white rounded-lg border p-4 min-h-[300px]">
                      <MermaidDiagram chart={data.diagramas_mermaid[diagramaActivo]} id={diagramaActivo} key={diagramaActivo} />
                    </div>
                  </CardContent>
                </Card>
              </>
            );
          })()}

          {/* ===== READY DEL SISTEMA ===== */}
          {seccionActiva === "ready" && (
            <>
              <Card className="border-t-4" style={{ borderTopColor: sec.color }}>
                <CardContent className="p-5">
                  <h3 className="text-lg font-bold text-slate-800 mb-1">Ready del Sistema</h3>
                  <p className="text-sm text-slate-500">Estado actual de configuración, endpoints y reglas de negocio</p>
                </CardContent>
              </Card>

              {/* Config */}
              <Card>
                <CardHeader className="pb-2"><CardTitle className="text-sm text-slate-700">Configuración Inicial</CardTitle></CardHeader>
                <CardContent className="p-4 pt-0">
                  <div className="space-y-1.5">
                    {data.ready.configuracion.map((c, i) => (
                      <div key={i} className="flex items-center gap-2 text-sm">
                        <CheckCircle2 className="w-4 h-4 text-green-500" />
                        <span className="text-slate-700">{c.item}</span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              {/* Endpoints */}
              <Card>
                <CardHeader className="pb-2"><CardTitle className="text-sm text-slate-700">Endpoints REST API</CardTitle></CardHeader>
                <CardContent className="p-4 pt-0">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="bg-slate-800 text-white">
                        <th className="px-2 py-1.5 text-left">Grupo</th>
                        <th className="px-2 py-1.5 text-left">Endpoints</th>
                        <th className="px-2 py-1.5 text-center">Estado</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.ready.endpoints.map((e, i) => (
                        <tr key={i} className={i % 2 === 0 ? "bg-slate-50" : ""}>
                          <td className="px-2 py-1.5 font-semibold text-slate-800">{e.grupo}</td>
                          <td className="px-2 py-1.5 font-mono text-slate-600">{e.endpoints}</td>
                          <td className="px-2 py-1.5 text-center">
                            <Badge className="bg-green-100 text-green-700 text-[10px]">
                              <CheckCircle2 className="w-3 h-3 mr-1" />{e.estado}
                            </Badge>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </CardContent>
              </Card>

              {/* Reglas de negocio */}
              <Card>
                <CardHeader className="pb-2"><CardTitle className="text-sm text-slate-700">Reglas de Negocio</CardTitle></CardHeader>
                <CardContent className="p-4 pt-0">
                  <div className="space-y-1.5">
                    {data.ready.reglas_negocio.map((r, i) => (
                      <div key={i} className="flex items-center gap-2 text-sm">
                        {r.estado === "completado" ? (
                          <CheckCircle2 className="w-4 h-4 text-green-500 flex-shrink-0" />
                        ) : (
                          <Clock className="w-4 h-4 text-amber-500 flex-shrink-0" />
                        )}
                        <span className={r.estado === "completado" ? "text-slate-700" : "text-amber-700"}>{r.regla}</span>
                        <Badge variant="outline" className={`text-[10px] ml-auto ${r.estado === "completado" ? "text-green-600" : "text-amber-600"}`}>
                          {r.estado}
                        </Badge>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              {/* Variables de entorno */}
              <Card>
                <CardHeader className="pb-2"><CardTitle className="text-sm text-slate-700">Variables de Entorno</CardTitle></CardHeader>
                <CardContent className="p-4 pt-0">
                  <table className="w-full text-xs">
                    <thead><tr className="bg-slate-100"><th className="px-2 py-1.5 text-left font-semibold">Variable</th><th className="px-2 py-1.5 text-left font-semibold">Descripción</th></tr></thead>
                    <tbody>
                      {data.ready.variables_entorno.map((v, i) => (
                        <tr key={i} className={i % 2 === 0 ? "" : "bg-slate-50"}>
                          <td className="px-2 py-1 font-mono text-violet-700 font-semibold">{v.variable}</td>
                          <td className="px-2 py-1 text-slate-600">{v.descripcion}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </CardContent>
              </Card>
            </>
          )}

          {/* ===== RECOMENDACIONES ===== */}
          {seccionActiva === "recomendaciones" && (
            <>
              <Card className="border-t-4" style={{ borderTopColor: sec.color }}>
                <CardContent className="p-5">
                  <h3 className="text-lg font-bold text-slate-800 mb-1">Recomendaciones Técnicas</h3>
                  <p className="text-sm text-slate-500">Arquitectura, autenticación y mejoras futuras</p>
                </CardContent>
              </Card>

              {/* Arquitectura */}
              <Card>
                <CardHeader className="pb-2"><CardTitle className="text-sm text-slate-700 flex items-center gap-2"><Server className="w-4 h-4" /> Arquitectura</CardTitle></CardHeader>
                <CardContent className="p-4 pt-0 space-y-3">
                  {data.recomendaciones.arquitectura.map((a, i) => (
                    <div key={i} className="bg-slate-50 rounded-lg p-3">
                      <h4 className="text-sm font-semibold text-slate-800">{a.titulo}</h4>
                      <p className="text-xs text-slate-600 mt-1">{a.detalle}</p>
                    </div>
                  ))}
                </CardContent>
              </Card>

              {/* Autenticación */}
              <Card>
                <CardHeader className="pb-2"><CardTitle className="text-sm text-slate-700 flex items-center gap-2"><Shield className="w-4 h-4" /> Autenticación</CardTitle></CardHeader>
                <CardContent className="p-4 pt-0">
                  <div className="grid grid-cols-2 gap-3">
                    {Object.entries(data.recomendaciones.autenticacion).map(([k, v]) => (
                      <div key={k} className="bg-slate-50 rounded-lg p-3">
                        <span className="text-xs font-semibold text-slate-500 uppercase">{k}</span>
                        <p className="text-sm text-slate-800 font-medium mt-0.5">{v}</p>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              {/* Estado Frontend */}
              <Card>
                <CardHeader className="pb-2"><CardTitle className="text-sm text-slate-700 flex items-center gap-2"><Layers className="w-4 h-4" /> Manejo de Estado (Frontend)</CardTitle></CardHeader>
                <CardContent className="p-4 pt-0">
                  <div className="grid grid-cols-3 gap-3">
                    {Object.entries(data.recomendaciones.estado_frontend).map(([k, v]) => (
                      <div key={k} className="bg-slate-50 rounded-lg p-3">
                        <span className="text-xs font-semibold text-slate-500 uppercase">{k}</span>
                        <p className="text-sm text-slate-800 mt-0.5">{v}</p>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              {/* Mejoras futuras */}
              <Card className="border-l-4 border-l-amber-400">
                <CardHeader className="pb-2"><CardTitle className="text-sm text-amber-700 flex items-center gap-2"><Lightbulb className="w-4 h-4" /> Mejoras Futuras</CardTitle></CardHeader>
                <CardContent className="p-4 pt-0">
                  <div className="space-y-1.5">
                    {data.recomendaciones.mejoras_futuras.map((m, i) => (
                      <div key={i} className="flex items-center gap-2 text-sm">
                        <ChevronRight className="w-4 h-4 text-amber-500" />
                        <span className="text-slate-700">{m}</span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default FlujogramasPage;
